import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Layers, LayoutDashboard, LogOut, PlusCircle, RefreshCw, ShieldCheck, User } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useProduct } from "@/lib/product-context";
import PresenceIndicator from "@/components/PresenceIndicator";
import { useState } from "react";

export default function Layout() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, signOut } = useAuth();
  const { meta, clearProduct } = useProduct();
  const [menuOpen, setMenuOpen] = useState(false);

  // Product-aware color tokens; safe defaults keep unauthenticated / no-product renders sane.
  const primaryBg = meta?.key === "tarklen"
    ? "bg-tarklen-blue hover:bg-tarklen-blue-dark"
    : "bg-keplerio-navy hover:bg-keplerio-navy-dark";
  const activeBg = meta?.key === "tarklen" ? "bg-tarklen-blue" : "bg-keplerio-navy";
  const activeText = "text-white";
  const inactiveText = meta?.key === "tarklen" ? "text-tarklen-blue-dark" : "text-keplerio-navy";
  const inactiveHover = meta?.key === "tarklen"
    ? "hover:bg-tarklen-blue/5 hover:border-tarklen-blue/10"
    : "hover:bg-keplerio-navy/5 hover:border-keplerio-navy/10";
  const accentBar = meta?.key === "tarklen" ? "bg-tarklen-green" : (meta?.swoosh || "bg-keplerio-gold");
  const accentText = meta?.key === "tarklen" ? "text-tarklen-purple" : "text-keplerio-teal";
  const envBadge = meta?.key === "tarklen"
    ? "bg-tarklen-green/15 text-tarklen-green-dark"
    : "bg-keplerio-gold/25 text-keplerio-navy-dark";

  const navItem = (to, label, Icon, testId) => {
    const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
    return (
      <Link
        to={to}
        data-testid={testId}
        title={label}
        className={`inline-flex items-center gap-2 px-2.5 md:px-3 py-2 text-sm font-medium border border-transparent transition-colors ${
          active
            ? `${activeBg} ${activeText}`
            : `${inactiveText} ${inactiveHover}`
        }`}
      >
        <Icon size={16} />
        <span className="hidden lg:inline">{label}</span>
      </Link>
    );
  };

  const switchProduct = () => {
    clearProduct();
    nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={`layout-${meta?.key || "none"}`}>
      <header
        data-testid="app-header"
        className="sticky top-0 z-40 bg-white border-b border-slate-200"
      >
        <div className={`h-1 w-full ${accentBar}`} />
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
          <Link to="/dashboard" data-testid="header-logo" className="flex items-center gap-3 shrink-0">
            {meta && (
              <img
                src={meta.logo}
                alt={meta.name}
                className="h-7 md:h-9 w-auto"
                data-testid="header-logo-img"
              />
            )}
            <div className="hidden sm:flex flex-col leading-tight border-l border-slate-200 pl-3">
              <span className={`overline text-[10px] tracking-[0.28em] ${accentText}`}>
                {meta ? `${meta.name} · ${meta.environment}` : "UAT · Tracker"}
              </span>
              <span className="font-heading text-xs font-semibold text-slate-900">
                Klystic Test Runner
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-2 md:gap-3">
            <nav className="flex items-center gap-1">
              {navItem("/dashboard", "Dashboard", LayoutDashboard, "nav-dashboard")}
              {navItem("/consolidated", "Consolidated", Layers, "nav-consolidated")}
              {navItem("/new", "New Session", PlusCircle, "nav-new-session")}
              {user && user.role === "admin" &&
                navItem("/admin/users", "Admin", ShieldCheck, "nav-admin")}
            </nav>

            {meta && (
              <button
                onClick={switchProduct}
                data-testid="switch-product-btn"
                className={`hidden md:inline-flex items-center gap-2 pl-2 pr-3 py-1.5 border border-slate-200 hover:border-slate-300 transition-colors ${envBadge}`}
                title="Switch product"
              >
                <RefreshCw size={11} />
                <span className="text-[10px] font-mono tracking-widest">
                  {meta.name.toUpperCase()} · {meta.environment}
                </span>
              </button>
            )}

            <div className="hidden md:block">
              <PresenceIndicator />
            </div>

            {user && (
              <div className="relative">
                <button
                  data-testid="user-menu-btn"
                  onClick={() => setMenuOpen((o) => !o)}
                  className={`inline-flex items-center gap-2 pl-2 pr-3 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-900 transition-colors`}
                >
                  <span className={`w-6 h-6 ${primaryBg.split(" ")[0]} text-white text-xs font-bold flex items-center justify-center`}>
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <span className="text-xs hidden md:inline max-w-[10rem] truncate">
                    {user.name || user.email}
                  </span>
                </button>
                {menuOpen && (
                  <div
                    data-testid="user-menu"
                    className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 shadow-lg z-50"
                    onMouseLeave={() => setMenuOpen(false)}
                  >
                    <div className="px-3 py-3 border-b border-slate-100">
                      <div className="text-xs text-slate-500 flex items-center gap-1">
                        <User size={11} /> Signed in as
                      </div>
                      <div className="text-sm text-slate-900 font-medium truncate">
                        {user.email}
                      </div>
                      {user.role === "admin" && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono tracking-widest text-keplerio-navy bg-keplerio-gold/40 px-1.5 py-0.5">
                          <ShieldCheck size={10} /> ADMIN
                        </div>
                      )}
                    </div>
                    <button
                      data-testid="switch-product-menu"
                      onClick={switchProduct}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2"
                    >
                      <RefreshCw size={14} /> Switch product
                    </button>
                    <button
                      data-testid="signout-btn"
                      onClick={() => {
                        signOut();
                        nav("/login", { replace: true });
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 inline-flex items-center gap-2"
                    >
                      <LogOut size={14} /> Sign out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Outlet />
      </main>

      <footer className="max-w-7xl mx-auto px-6 py-8 mt-8 border-t border-slate-200">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-slate-500">
          <div className="overline">Klystic UAT Tracker · v3.0</div>
          <div className="font-mono text-slate-700">
            {meta ? `${meta.name} · ${meta.environment}` : "Environment: —"}
          </div>
        </div>
      </footer>
    </div>
  );
}
